import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useLadder } from '../contexts/LadderContext';
import { Calendar, Users, Swords, ChevronRight, Flame, RotateCcw, TrendingUp, UserPlus, Clock, Target } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';

// Greek month names
const GREEK_MONTHS_FULL = ['Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος', 
                          'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος'];
const ENGLISH_MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June',
                             'July', 'August', 'September', 'October', 'November', 'December'];

// Greek month abbreviations for short date format
const GREEK_MONTHS = ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαϊ', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'];
const ENGLISH_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Weekday keys for translation lookup
const WEEKDAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Get relative date label for dashboard (Europe/Athens timezone)
const getRelativeDateLabel = (dateStr, t, language) => {
  if (!dateStr) return null;
  
  try {
    const matchDate = new Date(dateStr);
    const now = new Date();
    const athensFormatter = new Intl.DateTimeFormat('en-CA', { 
      timeZone: 'Europe/Athens',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    const todayStr = athensFormatter.format(now);
    const matchDateStr = athensFormatter.format(matchDate);
    
    const today = new Date(todayStr);
    const match = new Date(matchDateStr);
    
    const diffTime = today.getTime() - match.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return t('today');
    } else if (diffDays === 1) {
      return t('yesterday');
    } else if (diffDays > 1 && diffDays <= 6) {
      const weekdayKey = WEEKDAY_KEYS[matchDate.getDay()];
      return t(weekdayKey);
    } else {
      const day = matchDate.getDate();
      const monthIndex = matchDate.getMonth();
      const monthName = language === 'el' ? GREEK_MONTHS[monthIndex] : ENGLISH_MONTHS[monthIndex];
      return `${day} ${monthName}`;
    }
  } catch {
    return null;
  }
};

// Get current 2-month period info
const getCurrentPeriod = (language) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // 1-indexed
  
  // Determine period start month (odd months: 1, 3, 5, 7, 9, 11)
  const startMonth = month % 2 === 0 ? month - 1 : month;
  const endMonth = startMonth + 1;
  
  // Period end date
  const periodEndDate = new Date(year, endMonth, 0); // Last day of end month
  const daysRemaining = Math.max(0, Math.ceil((periodEndDate - now) / (1000 * 60 * 60 * 24)) + 1);
  
  // Month names
  const startMonthName = language === 'el' ? GREEK_MONTHS_FULL[startMonth - 1] : ENGLISH_MONTHS_FULL[startMonth - 1];
  const endMonthName = language === 'el' ? GREEK_MONTHS_FULL[endMonth - 1] : ENGLISH_MONTHS_FULL[endMonth - 1];
  
  // Short month names for compact display
  const startMonthShort = language === 'el' ? GREEK_MONTHS[startMonth - 1] : ENGLISH_MONTHS[startMonth - 1];
  const endMonthShort = language === 'el' ? GREEK_MONTHS[endMonth - 1] : ENGLISH_MONTHS[endMonth - 1];
  
  return {
    periodLabel: `${startMonthShort} - ${endMonthShort} ${year}`,
    daysRemaining
  };
};

// Calculate streaks from matches (returns full history for comeback detection)
const calculatePlayerHistory = (matches, playerId) => {
  const playerMatches = matches
    .filter(m => 
      m.team_a_player1_id === playerId ||
      m.team_a_player2_id === playerId ||
      m.team_b_player1_id === playerId ||
      m.team_b_player2_id === playerId
    )
    .sort((a, b) => {
      const dateA = a.order !== undefined ? a.order : (a.date ? new Date(a.date) : new Date(a.created_at));
      const dateB = b.order !== undefined ? b.order : (b.date ? new Date(b.date) : new Date(b.created_at));
      if (typeof dateA === 'number' && typeof dateB === 'number') return dateB - dateA;
      return new Date(dateB) - new Date(dateA);
    });

  if (playerMatches.length === 0) return { wins: 0, losses: 0, history: [], lastMatch: null };

  const history = playerMatches.map(match => {
    const isTeamA = match.team_a_player1_id === playerId || match.team_a_player2_id === playerId;
    const won = (isTeamA && match.winner_team === 'A') || (!isTeamA && match.winner_team === 'B');
    const partnerId = isTeamA 
      ? (match.team_a_player1_id === playerId ? match.team_a_player2_id : match.team_a_player1_id)
      : (match.team_b_player1_id === playerId ? match.team_b_player2_id : match.team_b_player1_id);
    return { won, partnerId, match };
  });

  let winStreak = 0;
  let lossStreak = 0;

  for (const h of history) {
    if (winStreak === 0 && lossStreak === 0) {
      if (h.won) winStreak = 1;
      else lossStreak = 1;
    } else if (winStreak > 0) {
      if (h.won) winStreak++;
      else break;
    } else if (lossStreak > 0) {
      if (!h.won) lossStreak++;
      else break;
    }
  }

  return { wins: winStreak, losses: lossStreak, history, lastMatch: playerMatches[0] };
};

// Check for comeback players (had 3+ losses then won RECENTLY) - returns up to 3
// Time-sensitive: only shows if the comeback win was in the last 7 days
const findComebackPlayers = (players, matches, getPlayerById) => {
  const comebackPlayers = [];
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  
  for (const player of players) {
    const { history } = calculatePlayerHistory(matches, player.id);
    if (history.length < 2) continue;
    
    // Must have won most recent match
    if (!history[0].won) continue;
    
    // Check if the comeback win is recent (within 7 days)
    const comebackMatchDate = history[0].match?.date 
      ? new Date(history[0].match.date) 
      : (history[0].match?.created_at ? new Date(history[0].match.created_at) : null);
    
    if (!comebackMatchDate || comebackMatchDate < sevenDaysAgo) continue;
    
    // Count consecutive losses before the win
    let lossesBeforeWin = 0;
    for (let i = 1; i < history.length; i++) {
      if (!history[i].won) lossesBeforeWin++;
      else break;
    }
    
    if (lossesBeforeWin >= 3) {
      const partner = getPlayerById(history[0].partnerId);
      comebackPlayers.push({
        playerId: player.id,
        playerName: player.name,
        partnerName: partner?.name?.split(' ')[0] || 'Partner',
        lossStreak: lossesBeforeWin
      });
    }
  }
  
  // Sort by loss streak descending, return top 3
  return comebackPlayers
    .sort((a, b) => b.lossStreak - a.lossStreak)
    .slice(0, 3);
};

// Removed - climbers now come from backend API

// Compact score display for home page
const CompactScoreCard = ({ match, getPlayerById, relativeDate }) => {
  const teamAP1 = getPlayerById(match.team_a_player1_id);
  const teamAP2 = getPlayerById(match.team_a_player2_id);
  const teamBP1 = getPlayerById(match.team_b_player1_id);
  const teamBP2 = getPlayerById(match.team_b_player2_id);
  const isTeamAWinner = match.winner_team === 'A';
  const sets = match.sets || [];
  const maxSets = 3;

  return (
    <div className="bg-card rounded-lg border border-border p-3">
      {relativeDate && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2 pb-2 border-b border-border">
          <Calendar className="h-3 w-3" />
          <span>{relativeDate}</span>
        </div>
      )}
      <div className="space-y-2">
        <div className={`flex items-center justify-between ${isTeamAWinner ? 'text-foreground' : 'text-muted-foreground'}`}>
          <div className={`flex items-center gap-2 min-w-0 flex-1 ${isTeamAWinner ? 'font-medium' : ''}`}>
            {isTeamAWinner && <div className="w-0.5 h-4 bg-green-500 rounded-full flex-shrink-0" />}
            <span className="text-sm truncate">
              {teamAP1?.name?.split(' ')[0]} / {teamAP2?.name?.split(' ')[0]}
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
            {Array.from({ length: maxSets }).map((_, idx) => (
              <span 
                key={`home-set-a-${idx}`} 
                className={`text-xs font-mono w-4 text-center ${
                  sets[idx] 
                    ? sets[idx].team_a > sets[idx].team_b 
                      ? 'text-green-600 dark:text-green-400 font-semibold' 
                      : ''
                    : 'invisible'
                }`}
              >
                {sets[idx]?.team_a ?? '-'}
              </span>
            ))}
          </div>
        </div>
        <div className={`flex items-center justify-between ${!isTeamAWinner ? 'text-foreground' : 'text-muted-foreground'}`}>
          <div className={`flex items-center gap-2 min-w-0 flex-1 ${!isTeamAWinner ? 'font-medium' : ''}`}>
            {!isTeamAWinner && <div className="w-0.5 h-4 bg-green-500 rounded-full flex-shrink-0" />}
            <span className="text-sm truncate">
              {teamBP1?.name?.split(' ')[0]} / {teamBP2?.name?.split(' ')[0]}
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
            {Array.from({ length: maxSets }).map((_, idx) => (
              <span 
                key={`home-set-b-${idx}`} 
                className={`text-xs font-mono w-4 text-center ${
                  sets[idx] 
                    ? sets[idx].team_b > sets[idx].team_a 
                      ? 'text-green-600 dark:text-green-400 font-semibold' 
                      : ''
                    : 'invisible'
                }`}
              >
                {sets[idx]?.team_b ?? '-'}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Home() {
  const { t, language } = useLanguage();
  const { ladder, players, matches, leaderboard, loading, getPlayerById } = useLadder();

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="home-loading">
        <Skeleton className="h-24 rounded-xl mb-4" />
        <Skeleton className="h-20 rounded-xl mb-4" />
        <Skeleton className="h-48 rounded-xl mb-4" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  // Get period from leaderboard API (includes admin-configured period)
  // Fallback to local calculation if not available
  const apiPeriod = !Array.isArray(leaderboard) ? leaderboard?.period : null;
  const period = apiPeriod ? {
    periodLabel: (() => {
      // Check if period is configured
      if (apiPeriod.is_configured === false) {
        return language === 'el' ? 'Δεν έχει οριστεί' : 'Not configured';
      }
      
      // Check if date-based period
      if (apiPeriod.is_date_based && apiPeriod.start_date && apiPeriod.end_date) {
        const startDate = new Date(apiPeriod.start_date);
        const endDate = new Date(apiPeriod.end_date);
        const startDay = startDate.getDate();
        const endDay = endDate.getDate();
        const startMonthName = (language === 'el' ? GREEK_MONTHS : ENGLISH_MONTHS)[startDate.getMonth()];
        const endMonthName = (language === 'el' ? GREEK_MONTHS : ENGLISH_MONTHS)[endDate.getMonth()];
        
        // Same month
        if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
          return `${startDay} - ${endDay} ${startMonthName} ${startDate.getFullYear()}`;
        }
        // Same year
        if (startDate.getFullYear() === endDate.getFullYear()) {
          return `${startDay} ${startMonthName} - ${endDay} ${endMonthName} ${startDate.getFullYear()}`;
        }
        // Different years
        return `${startDay} ${startMonthName} ${startDate.getFullYear()} - ${endDay} ${endMonthName} ${endDate.getFullYear()}`;
      }
      
      // Month-based period (legacy) - only if months are defined
      if (apiPeriod.start_month && apiPeriod.end_month) {
        const startMonthName = (language === 'el' ? GREEK_MONTHS : ENGLISH_MONTHS)[apiPeriod.start_month - 1];
        const endMonthName = (language === 'el' ? GREEK_MONTHS : ENGLISH_MONTHS)[apiPeriod.end_month - 1];
        // Handle December-January wrap (show both years)
        if (apiPeriod.start_month === 12 && apiPeriod.end_month === 1) {
          return `${startMonthName} ${apiPeriod.year} - ${endMonthName} ${apiPeriod.year + 1}`;
        }
        return `${startMonthName} - ${endMonthName} ${apiPeriod.year}`;
      }
      
      // Fallback for unconfigured period
      return language === 'el' ? 'Δεν έχει οριστεί' : 'Not configured';
    })(),
    daysRemaining: apiPeriod.is_configured === false ? null : apiPeriod.days_remaining,
    isConfigured: apiPeriod.is_configured !== false
  } : getCurrentPeriod(language);

  // Filter matches by current period dates
  const currentPeriodMatches = (() => {
    // If period not configured, return empty array
    if (!ladder?.period_start_date || !ladder?.period_end_date) {
      return [];
    }
    
    const periodStart = new Date(ladder.period_start_date);
    const periodEnd = new Date(ladder.period_end_date);
    periodEnd.setHours(23, 59, 59, 999); // Include entire end day
    
    return matches.filter(m => {
      const matchDate = m.date ? new Date(m.date) : new Date(m.created_at);
      return matchDate >= periodStart && matchDate <= periodEnd;
    });
  })();

  // Handle both old (array) and new (object) leaderboard formats
  const qualified = Array.isArray(leaderboard) ? leaderboard : (leaderboard?.qualified || []);
  const newEntries = Array.isArray(leaderboard) ? [] : (leaderboard?.new_entries || []);
  const totalNewEntries = Array.isArray(leaderboard) ? 0 : (leaderboard?.total_new_entries || 0);
  const almostThere = Array.isArray(leaderboard) ? [] : (leaderboard?.almost_there || []);
  const climbers = Array.isArray(leaderboard) ? [] : (leaderboard?.climbers || []);

  // Get top 3 qualified players
  const top3 = qualified.slice(0, 3);

  // Get players with 3+ win streaks (On Fire) - limit to 3
  // Uses current period matches only
  const onFirePlayers = qualified
    .map(player => {
      const { wins } = calculatePlayerHistory(currentPeriodMatches, player.player_id);
      return { ...player, streak: wins };
    })
    .filter(p => p.streak >= 3)
    .sort((a, b) => b.streak - a.streak)
    .slice(0, 3);

  // Find comeback players (up to 3) - uses current period matches only
  const comebackPlayers = findComebackPlayers(players, currentPeriodMatches, getPlayerById);

  // Check if any highlights exist
  const hasHighlights = onFirePlayers.length > 0 || comebackPlayers.length > 0 || climbers.length > 0;

  // Get latest 3 matches from current period only
  const latestMatches = currentPeriodMatches.slice(0, 3);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="home-page">
      {/* Row 1: Current Period & Days Remaining */}
      <div className="bg-card rounded-xl border border-border p-5 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <Calendar className="h-5 w-5 text-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t('currentPeriod')} #{ladder?.period_number || 1}
              </p>
              <p className="font-heading font-semibold text-foreground">{period.periodLabel}</p>
            </div>
          </div>
          <div className="text-right">
            {period.daysRemaining !== null && period.daysRemaining !== undefined ? (
              period.daysRemaining <= 0 ? (
                <>
                  <p className="text-lg font-bold text-amber-600 dark:text-amber-400">
                    {language === 'el' ? 'Ολοκληρώθηκε' : 'Completed'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'el' ? 'Περιμένει λήξη' : 'Awaiting close'}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold text-foreground">{period.daysRemaining}</p>
                  <p className="text-xs text-muted-foreground">
                    {period.daysRemaining === 1 ? t('dayRemaining') : t('daysRemaining')}
                  </p>
                </>
              )
            ) : (
              <p className="text-sm text-muted-foreground">
                {language === 'el' ? 'Ορίστε περίοδο' : 'Set period dates'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Row 2: Total Players & Matches - Clickable */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Link 
          to="/partners" 
          className="bg-card rounded-xl border border-border p-4 flex items-center gap-3 hover:bg-muted/50 hover:border-primary/50 transition-colors cursor-pointer"
          data-testid="total-players-card"
        >
          <div className="p-2 rounded-lg bg-muted">
            <Users className="h-5 w-5 text-foreground" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">{players.length}</p>
            <p className="text-xs text-muted-foreground">{t('totalPlayers')}</p>
          </div>
        </Link>
        <Link 
          to="/scores" 
          className="bg-card rounded-xl border border-border p-4 flex items-center gap-3 hover:bg-muted/50 hover:border-primary/50 transition-colors cursor-pointer"
          data-testid="total-matches-card"
        >
          <div className="p-2 rounded-lg bg-muted">
            <Swords className="h-5 w-5 text-foreground" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">{currentPeriodMatches.length}</p>
            <p className="text-xs text-muted-foreground">{t('totalMatches')}</p>
          </div>
        </Link>
      </div>

      {/* Row 3: Top 3 Qualified Players */}
      <div className="bg-card rounded-xl border border-border p-5 mb-4">
        <h2 className="font-heading font-semibold text-foreground mb-4">{t('topPlayers')}</h2>
        {top3.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{t('noQualifiedPlayers')}</p>
        ) : (
          <div className="space-y-3">
            {top3.map((player, idx) => (
              <div key={player.player_id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400' :
                    idx === 1 ? 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300' :
                    'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="font-medium text-foreground">{player.player_name}</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {player.total_points} {t('pts')}
                </span>
              </div>
            ))}
          </div>
        )}
        <Link to="/leaderboard" className="block mt-4">
          <Button variant="outline" className="w-full justify-center gap-2">
            {t('viewFullLeaderboard')}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>

      {/* The Hunted - Defending Champion Card */}
      {ladder?.defending_champion_id && (() => {
        const champion = getPlayerById(ladder.defending_champion_id);
        if (!champion) return null;
        return (
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-xl border border-amber-200 dark:border-amber-800/30 p-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/40">
                <Target className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                  {t('theHunted')}
                </p>
                <p className="font-heading font-semibold text-foreground">
                  {champion.display_name || champion.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('defendingChampion')}
                </p>
              </div>
              <span className="text-amber-500 font-bold text-2xl">Ⓒ</span>
            </div>
          </div>
        );
      })()}

      {/* Highlights Section - On Fire, Comeback, Climber */}
      {hasHighlights && (
        <div className="space-y-3 mb-4">
          {/* On Fire */}
          {onFirePlayers.length > 0 && (
            <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl border border-orange-200 dark:border-orange-800/30 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Flame className="h-4 w-4 text-orange-500" />
                <h3 className="font-semibold text-foreground text-sm">{t('onFire')}</h3>
              </div>
              <div className="space-y-2">
                {onFirePlayers.map(player => (
                  <div 
                    key={player.player_id}
                    className="bg-card rounded-lg border border-border p-3"
                  >
                    <p className="font-medium text-foreground text-sm">{player.player_name}</p>
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">
                      {player.streak} {t('winStreak')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comeback */}
          {comebackPlayers.length > 0 && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200 dark:border-purple-800/30 p-4">
              <div className="flex items-center gap-2 mb-3">
                <RotateCcw className="h-4 w-4 text-purple-600" />
                <h3 className="font-semibold text-foreground text-sm">{t('comeback')}</h3>
              </div>
              <div className="space-y-2">
                {comebackPlayers.map(player => (
                  <div 
                    key={player.playerId}
                    className="bg-card rounded-lg border border-border p-3"
                  >
                    <p className="font-medium text-foreground text-sm">{player.playerName}</p>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                      {t('comebackSubtitle').replace('{0}', player.lossStreak).replace('{1}', player.partnerName)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Climber */}
          {climbers.length > 0 && (
            <div className="bg-gradient-to-r from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 rounded-xl border border-sky-200 dark:border-sky-800/30 p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-sky-600" />
                <h3 className="font-semibold text-foreground text-sm">{t('climber')}</h3>
              </div>
              <div className="space-y-2">
                {climbers.map(player => (
                  <div 
                    key={player.player_id}
                    className="bg-card rounded-lg border border-border p-3"
                  >
                    <p className="font-medium text-foreground text-sm">{player.player_name}</p>
                    <p className="text-xs text-sky-600 dark:text-sky-400 mt-0.5">
                      {t('climberSubtitle').replace('{0}', player.climb)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* New Entry & Almost There Cards */}
      {(newEntries.length > 0 || almostThere.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          {/* New Entry */}
          {newEntries.length > 0 && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-800/30 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-foreground text-sm">{t('newEntry')}</h3>
                </div>
                {totalNewEntries > 3 && (
                  <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                    +{totalNewEntries - 3} {language === 'el' ? 'ακόμα' : 'more'}
                  </span>
                )}
              </div>
              <div className="space-y-2">
                {newEntries.slice(0, 3).map(player => (
                  <div 
                    key={player.player_id}
                    className="bg-card rounded-lg border border-border p-3"
                  >
                    <p className="font-medium text-foreground text-sm">{player.player_name}</p>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                      {t('nowRanked')} #{player.rank}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Almost There */}
          {almostThere.length > 0 && (
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-xl border border-amber-200 dark:border-amber-800/30 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4 text-amber-600" />
                <h3 className="font-semibold text-foreground text-sm">{t('almostThere')}</h3>
              </div>
              <div className="space-y-2">
                {almostThere.slice(0, 3).map(player => (
                  <div 
                    key={player.player_id}
                    className="bg-card rounded-lg border border-border p-3"
                  >
                    <p className="font-medium text-foreground text-sm">{player.player_name}</p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                      {t('oneMoreMatch')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Latest Scores */}
      <div className="bg-card rounded-xl border border-border p-5">
        <h2 className="font-heading font-semibold text-foreground mb-4">{t('latestScores')}</h2>
        {latestMatches.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{t('noMatches')}</p>
        ) : (
          <div className="space-y-3">
            {latestMatches.map(match => (
              <CompactScoreCard 
                key={match.id} 
                match={match} 
                getPlayerById={getPlayerById}
                relativeDate={getRelativeDateLabel(match.date, t, language)}
              />
            ))}
          </div>
        )}
        <Link to="/scores" className="block mt-4">
          <Button variant="outline" className="w-full justify-center gap-2">
            {t('viewAllScores')}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
